import { useCallback, useEffect, useState } from 'react'

import { useLocation } from '@tanstack/react-router'

/**
 * A hook that persists a state value to localStorage, scoped to the current route path.
 *
 * @template T the thing being stored
 * @param stateKey page-local unique key for the value
 * @param defaultValue the default value if nothing is in storage
 */
export const usePersistedValue = <T>(
  stateKey: string,
  defaultValue: T,
): [T, (value: T) => void] => {
  const location = useLocation()
  const getStorageKey = useCallback(
    (key: string) => `${key}_${location.pathname}`,
    [location.pathname],
  )
  const getStoredValue = (key: string, defaultVal: T): T => {
    try {
      const stored = localStorage.getItem(getStorageKey(key))
      return stored !== null ? (JSON.parse(stored) as T) : defaultVal
    } catch {
      return defaultVal
    }
  }
  const [value, setValue] = useState(() =>
    getStoredValue(stateKey, defaultValue),
  )
  useEffect(() => {
    localStorage.setItem(getStorageKey(stateKey), JSON.stringify(value))
  }, [getStorageKey, stateKey, value])
  return [value, setValue]
}
